/**
 * Copyright (c) 2020-2021 Perficient, Inc.
 * Unauthorized copying or distribution of this file prohibited without the express permission of Perficient.
 */

import { errorToObject, v2 } from "@prftesp/cle-logger";
import React, { useState, useCallback } from "react";
import errorCodes from "../../../../utility/errorCodes";
import {
	endEmailInteraction,
	updateEmailInteraction,
	getAttachmentUploadUrl,
	getContactEmails,
	getEmailAttachment,
	sendEmail,
	uploadAttachment,
} from "../../services/EmailService";
import { CircularProgress, withStyles } from "@material-ui/core";
import styles from "./styles";
import { getAttributeValueByName } from "../../services/Attributes";
import { useInterval, useEffectOnce } from "../../../../hooks";
import EmailList from "./EmailList";
import { useCurrentContactEvents } from "../../hooks/useCurrentContactEvents";
import { saveAs } from "file-saver";
import NewEmail from "./NewEmail";

const COMPONENT_NAME = "CCP components/Email";

/**
 * Displays email communication
 * @param {Object} props
 * @param {Object} props.classes classes injected using MUI withStyles
 * @param {Object} props.contact current contact
 * @param {(shouldShow, message, level) => void} props.renderToastContent displays a toast message with the given level (success, warning or danger)
 */
const Email = ({ classes, contact, renderToastContent }) => {
	const [emails, setEmails] = useState({});
	const inquiryId = contact ? getAttributeValueByName(contact.attributes, "hidden_inquiryId", "") : null;

	const handleContactEnded = useCallback(
		(contact) => {
			if (contact.getState().type !== "ended") {
				// to prevent missed contacts from calling the API
				return;
			}

			endEmailInteraction(inquiryId, emails[inquiryId][0].date)
				.then(() => {
					const newEmails = { ...emails };
					delete newEmails[inquiryId];
					setEmails(newEmails);
				})
				.catch((error) => {
					v2.error("Error while ending email interaction.", {
						additionalData: { error: errorToObject(error) },
						componentName: COMPONENT_NAME,
						logCode: errorCodes.END_EMAIL_INTERACTION_ERROR,
					});
				});
		},
		[emails, inquiryId],
	);
	useCurrentContactEvents(window.connect.ContactEvents.ENDED, handleContactEnded);

	// in the case of a transfer, we want to track the new contact id and make sure
	// the dyanmodb record in the backend reflects that the contact is not completed
	// surely there's a better way to check for a transfer status...
	const handleContactConnected = useCallback(
		(contact) => {
			const initialContactId = contact.getInitialContactId();
			if (initialContactId) {
				updateEmailInteraction(inquiryId, emails[inquiryId][0].date, contact.contactId).catch((error) => {
					v2.error("Error while updating email interaction.", {
						additionalData: { error: errorToObject(error) },
						componentName: COMPONENT_NAME,
						logCode: errorCodes.UPDATE_EMAIL_INTERACTION_ERROR,
					});
				});
			}
		},
		[emails, inquiryId],
	);
	useCurrentContactEvents(window.connect.ContactEvents.CONNECTED, handleContactConnected);

	const fetchEmails = useCallback(() => {
		if (!inquiryId) {
			const error = new Error("Inquiry ID is null, contact is possibly not loaded properly");
			v2.error("Error while loading emails.", {
				additionalData: { error: errorToObject(error) },
				componentName: COMPONENT_NAME,
				logCode: errorCodes.NO_INQUIRY_ID_ERROR,
			});

			return;
		}

		const lastEmailDate = emails[inquiryId] && emails[inquiryId].length > 0 ? emails[inquiryId][0].date : null;
		getContactEmails(inquiryId, lastEmailDate)
			.then((fetchedEmails) => {
				if (fetchedEmails.length < 1) {
					return;
				}

				const newContactEmails = lastEmailDate ? [...emails[inquiryId], ...fetchedEmails] : fetchedEmails;
				const sorted = newContactEmails.sort((a, b) => (new Date(a.date) > new Date(b.date) ? -1 : 1));
				const newEmails = { ...emails, [inquiryId]: sorted };
				setEmails(newEmails);

				if (lastEmailDate && sorted[0].direction !== "outbound") {
					renderToastContent(true, "There are new emails.", "info");
				}
			})
			.catch((error) => {
				renderToastContent(true, "Failed to load emails.", "error");
				v2.error("Error while loading emails.", {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_EMAILS_ERROR,
				});
			});
	}, [inquiryId, renderToastContent, emails]);

	// fetch emails and then again every 5 seconds
	useEffectOnce(() => fetchEmails());
	useInterval(fetchEmails, 5000);

	const handleUploadAttachment = useCallback(
		async (attachment) => {
			const signedUrl = await getAttachmentUploadUrl(inquiryId, attachment.name);
			await uploadAttachment(signedUrl, attachment);
		},
		[inquiryId],
	);

	const handleSendEmail = useCallback(
		async (body, attachments) => {
			if (!inquiryId) {
				const error = new Error("Inquiry ID is null, contact is possibly not loaded properly");
				v2.error("Error while sending email reply.", {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.NO_INQUIRY_ID_ERROR,
				});

				return;
			}

			try {
				// upload all attachments to s3 first
				await Promise.all(Array.from(attachments).map(handleUploadAttachment));

				// send email and pass attachment names to be included
				const attachmentNames =
					attachments.length > 0 ? Array.from(attachments).map((attachment) => attachment.name) : null;
				await sendEmail(inquiryId, body, attachmentNames);

				renderToastContent(true, "Successfully sent email reply.", "success");
				fetchEmails();
			} catch (error) {
				renderToastContent(true, "Failed to send email reply.", "error");
				v2.error("Error while sending email reply.", {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.SEND_EMAIL_ERROR,
				});
			}
		},
		[fetchEmails, inquiryId, renderToastContent, handleUploadAttachment],
	);

	const handleDownloadAttachment = useCallback(
		async (email, attachment) => {
			try {
				// download from existing link or get new link
				const { fileName, signedUrl } = attachment;

				let downloadUrl;
				if (signedUrl) {
					// check expiry of existing signed url
					const url = new URL(signedUrl);
					const expiry = url.searchParams.get("Expires");
					const now = Math.floor(new Date().valueOf() / 1000);
					if (expiry > now) {
						// not expired
						downloadUrl = signedUrl;
					}
				}

				downloadUrl = downloadUrl || (await getEmailAttachment(inquiryId, email.date, fileName));
				saveAs(downloadUrl, fileName);

				if (signedUrl !== downloadUrl) {
					// store new signed url in attachment
					const newContactEmails = [...emails[inquiryId]];
					const emailIdx = newContactEmails.findIndex((e) => e.messageId === email.messageId);
					const attachmentIdx = newContactEmails[emailIdx].attachments.findIndex((a) => a.fileName === fileName);
					newContactEmails[emailIdx].attachments[attachmentIdx].signedUrl = downloadUrl;
					const newEmails = { ...emails, [inquiryId]: newContactEmails };
					setEmails(newEmails);
				}
			} catch (error) {
				renderToastContent(true, "Failed to download attachment.", "error");
				v2.error("Error while downloading email attachment.", {
					additionalData: { error: errorToObject(error) },
					componentName: COMPONENT_NAME,
					logCode: errorCodes.GET_EMAIL_ATTACHMENT_ERROR,
				});
			}
		},
		[emails, inquiryId, renderToastContent],
	);

	return (
		<div className={classes.root}>
			<NewEmail classes={classes} handleSendEmail={handleSendEmail} renderToastContent={renderToastContent} />

			<div className={classes.emailsContainer}>
				{emails[inquiryId] && emails[inquiryId].length > 0 ? (
					<EmailList
						classes={classes}
						emails={emails[inquiryId]}
						inquiryId={inquiryId}
						handleDownloadAttachment={handleDownloadAttachment}
					/>
				) : (
					<div className={classes.progressContainer}>
						<CircularProgress color="inherit" />
					</div>
				)}
			</div>
		</div>
	);
};

export default withStyles(styles)(Email);
